import React, { useState } from 'react';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const ComplianceForgeLanding: React.FC = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const pricingTiers: PricingTier[] = [
    {
      name: 'Standard',
      price: '$999',
      period: '/month',
      features: [
        'GDPR/CCPA automation',
        'Basic audit trails',
        'Risk assessment dashboard',
        '5 privacy agents',
        'Email support',
        'Up to 10 users'
      ],
      cta: 'Start Free Trial'
    },
    {
      name: 'Enterprise',
      price: '$2,499',
      period: '/month',
      features: [
        'All Standard features',
        'All 9 privacy agents enabled',
        'Advanced risk scoring',
        'Custom compliance frameworks',
        'SSO/SAML integration',
        'Dedicated success manager',
        'Unlimited users',
        'SLA guarantee'
      ],
      cta: 'Contact Sales',
      highlighted: true
    }
  ];

  const privacyAgents = [
    { name: 'ConsentManagementAgent', description: 'Automated consent tracking and preference management' },
    { name: 'DataGovernanceAgent', description: 'Data classification and governance policies' },
    { name: 'DataMappingAgent', description: 'Automated data flow mapping and inventory' },
    { name: 'DataSubjectRightsAgent', description: 'DSR request processing and fulfillment' },
    { name: 'EquilateralAITransferAgent', description: 'Cross-border transfer compliance' },
    { name: 'GovernmentDataProcessingAgent', description: 'Government and public sector compliance' },
    { name: 'PrivacyImpactAgent', description: 'Privacy impact assessments (PIA/DPIA)' },
    { name: 'PrivacyTrainingAgent', description: 'Compliance training and awareness' },
    { name: 'RetentionPolicyAgent', description: 'Data retention and deletion automation' }
  ];

  const securityBadges = [
    'SOC 2 Type II',
    'ISO 27001',
    'GDPR Compliant',
    'CCPA Compliant',
    'HIPAA Ready'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Automated Compliance for Regulated Industries
            </h1>
            <p className="text-xl md:text-2xl text-emerald-100 mb-8 max-w-3xl mx-auto">
              Transform your compliance operations with AI-powered automation.
              Reduce audit preparation time by 80% and stay ahead of regulatory changes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-white text-emerald-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-50 transition-colors"
                onClick={() => setShowContactForm(true)}
              >
                Request Demo
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors">
                View Documentation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Security Badges */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6">
            {securityBadges.map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium text-gray-700">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Built for Compliance Professionals
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">Compliance Officers</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  Automated regulatory monitoring and alerts
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  One-click audit report generation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  Cross-framework compliance mapping
                </li>
              </ul>
            </div>
            <div className="bg-emerald-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">Security Teams</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  Continuous risk assessment scoring
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  Automated vulnerability compliance checks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-1">-</span>
                  Incident response documentation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Enterprise Compliance Automation
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">GDPR/CCPA Automation</h3>
              <p className="text-gray-600">
                Automated data subject requests, consent management, and cross-border transfer compliance.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Audit Trails</h3>
              <p className="text-gray-600">
                Immutable audit logs with automated evidence collection and report generation for any framework.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Risk Assessment</h3>
              <p className="text-gray-600">
                Real-time risk scoring with AI-powered threat detection and remediation recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Agents */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            9 Specialized Privacy Agents
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            AI-powered agents work 24/7 to automate your compliance operations
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {privacyAgents.map((agent) => (
              <div key={agent.name} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-emerald-700 mb-1">{agent.name}</h4>
                <p className="text-sm text-gray-600">{agent.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Enterprise Pricing
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`bg-white rounded-xl shadow-sm p-8 ${
                  tier.highlighted ? 'ring-2 ring-emerald-600' : ''
                }`}
              >
                {tier.highlighted && (
                  <span className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mt-4">{tier.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600">{tier.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    tier.highlighted
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                  onClick={() => tier.highlighted && setShowContactForm(true)}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 mt-8">
            All plans include 14-day free trial. Enterprise contracts available.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Automate Your Compliance?
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Join leading enterprises who have reduced compliance overhead by 80% with ComplianceForge.
          </p>
          <button
            className="bg-white text-emerald-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-50 transition-colors"
            onClick={() => setShowContactForm(true)}
          >
            Schedule a Demo
          </button>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Sales</h3>
            <p className="text-gray-600 mb-6">
              Our enterprise team will reach out within 24 hours.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Work Email"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Company"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent"
              />
              <select className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent">
                <option>Select Team Size</option>
                <option>1-10</option>
                <option>11-50</option>
                <option>51-200</option>
                <option>200+</option>
              </select>
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => setShowContactForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceForgeLanding;
